import React, { Suspense, lazy } from 'react';
import ReactDOM from 'react-dom/client';
import * as Sentry from '@sentry/react';
import { CssBaseline, ThemeProvider } from '@mui/material';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import App from './App';
import { AuthProvider } from './auth/AuthContext';
import { installChunkReloadGuard } from './chunkReloadGuard';
import { installClientErrorBeacon, reportClientError } from './clientErrorBeacon';
import CrashFallback from './components/CrashFallback';
import LoadingScreen from './components/LoadingScreen';
import NewVersionNotice from './components/NewVersionNotice';
import theme from './theme';
import { initSentry } from './sentry';
import './styles.css';

// First, ahead of initSentry(): the whole point of the beacon is to hold a
// record when Sentry does not, which includes the case where Sentry's own
// initialization is what failed.
installClientErrorBeacon();
initSentry();
// Before any lazy chunk can fail: reload once when a dynamic import breaks
// transiently instead of stranding the user on the ErrorBoundary (LUTIQ-74).
installChunkReloadGuard();

const OnboardingWizardPage = lazy(() => import('./onboarding/OnboardingWizardPage'));
const McpDocsPage = lazy(() => import('./pages/McpDocsPage'));

/**
 * Some routes are fully public: they must render without booting Keycloak
 * (AuthProvider's mount effect calls initKeycloak, which enforces a secure
 * context and may redirect). These single-route shells never need the
 * authenticated router.
 *
 *   /signup    → self-service onboarding wizard (exits via full page loads).
 *   /docs/mcp  → the MCP connector docs page. PUBLIC by requirement: it is the
 *                Anthropic Connector Directory's Documentation URL, which must
 *                be reachable WITHOUT login. Keep it outside AuthProvider.
 *
 * The `dashboard-static` Cloudflare Worker serves any extensionless path by
 * falling back to index.html, so deep links to these routes boot the SPA here.
 */
const { pathname } = window.location;
const isPublicSignupRoute = pathname === '/signup';
const isPublicDocsRoute = pathname === '/docs/mcp';

const renderPublicRoute = (element: React.ReactNode) => (
  <BrowserRouter>
    <Suspense fallback={<LoadingScreen />}>
      <Routes>
        <Route path="*" element={element} />
      </Routes>
    </Suspense>
  </BrowserRouter>
);

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    {/* onError feeds the beacon directly: a render crash the boundary catches
        never reaches window.onerror in a production React build, so the window
        listener alone would miss exactly the failures that blank the page.
        reportClientError de-duplicates by error identity, so the development
        build's extra re-throw to window.onerror still counts as one. */}
    <Sentry.ErrorBoundary fallback={<CrashFallback />} onError={reportClientError}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {isPublicSignupRoute ? (
          renderPublicRoute(<OnboardingWizardPage />)
        ) : isPublicDocsRoute ? (
          renderPublicRoute(<McpDocsPage />)
        ) : (
          <AuthProvider>
            <App />
          </AuthProvider>
        )}
        {/* Outside the route branches on purpose: a tab left open on the
            signup wizard or the docs page goes stale the same way the
            dashboard does. */}
        <NewVersionNotice />
      </ThemeProvider>
    </Sentry.ErrorBoundary>
  </React.StrictMode>
);
